(function () {
  const defaultCartData = {
    attributes: {},
    cart_level_discount_applications: [],
    currency: "",
    item_count: 0,
    items: [],
    items_subtotal_price: "",
    note: null,
    original_total_price: 0,
    requires_shipping: false,
    token: null,
    total_discount: 0,
    total_price: 0,
    total_weight: 0,
  };

  const CartController = {
    cartData: { ...defaultCartData },

    shouldSkipAutoAdd() {
      const autoAddSkipParam = "skip_auto_add";

      // If the session cookie exists, never auto-add products.
      return document.cookie
        .split(";")
        .map((cookie) => cookie.trim())
        .some((cookie) => cookie.startsWith(`${autoAddSkipParam}=`));
    },

    getBlocksWithTriggerProducts() {
      return (window.autoAddToCartBlocks || []).filter(
        (block) =>
          Array.isArray(block.trigger_products) &&
          block.trigger_products.length > 0 &&
          block.gift_add_type === "add_all"
      );
    },

    async getCartData() {
      const response = await fetch("/cart.js", {
        headers: { Accept: "application/json" },
      });
      return response.json();
    },

    async init() {
      document.addEventListener("itemsAddedToCart", () => {
        this.setCartData();
        document
          .querySelectorAll('form[action="/cart/add"] button[type="submit"]')
          .forEach((button) => {
            button.disabled = false;
          });
      });

      document.addEventListener("cartUpdated", () => {
        this.setCartData();
      });

      await this.setCartData();
      document.dispatchEvent(new CustomEvent("cartReady"));
    },

    async setCartData() {
      const data = await this.getCartData();
      this.cartData = { ...data };
      document.dispatchEvent(new CustomEvent("cartDataUpdated"));
      return this.cartData;
    },

    async appendTriggerGiftItems(variantId, quantity, items) {
      if (!variantId || !quantity || !Array.isArray(items)) {
        return items;
      }

      // Keep trigger-based gift auto-add aligned with page-level auto-add behavior.
      if (this.shouldSkipAutoAdd()) {
        return items;
      }

      const blocksWithTriggerProducts = this.getBlocksWithTriggerProducts();
      if (!blocksWithTriggerProducts.length) {
        return items;
      }

      const cartData = this.cartData?.items?.length
        ? this.cartData
        : await this.getCartData();

      for (let i = 0; i < blocksWithTriggerProducts.length; i += 1) {
        const block = blocksWithTriggerProducts[i];
        const triggerProduct = (block.trigger_products || []).find(
          (product) => String(product.variant_id) === String(variantId)
        );
        const isCustomerOnly = block.is_customer_only;
        const isCustomer = window.isCustomer;

        if (!triggerProduct || (isCustomerOnly && !isCustomer)) {
          continue;
        }

        const triggerProductQuantity = Number(triggerProduct.quantity) || 1;
        const currentTriggerQuantity = cartData.items
          .filter(
            (item) => Number(item.variant_id) === Number(triggerProduct.variant_id)
          )
          .map((item) => Number(item.quantity))
          .reduce((a, b) => a + b, 0);

        if (
          triggerProductQuantity !== 1 &&
          currentTriggerQuantity + Number(quantity) !== triggerProductQuantity
        ) {
          continue;
        }

        (block.products || []).forEach((product) => {
          const currentGiftQuantity = cartData.items
            .filter(
              (item) => Number(item.variant_id) === Number(product.variant_id)
            )
            .map((item) => Number(item.quantity))
            .reduce((a, b) => a + b, 0);

          const giftTargetQuantity = Number(product.quantity) || 1;
          if (currentGiftQuantity >= giftTargetQuantity) {
            return;
          }

          items.push({
            id: product.variant_id,
            quantity: giftTargetQuantity - currentGiftQuantity,
            properties: { gift: true },
          });
        });

        break;
      }

      return items;
    },

    async addItems(items, openDrawer = true) {
      if (!Array.isArray(items) || !items.length) {
        throw new Error("CartController.addItems: items are required.");
      }

      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json; charset=utf-8",
          Accept: "application/json",
        },
        body: JSON.stringify({
          items: items.map((item) => ({
            id: item.id,
            quantity: item.quantity,
            properties: item.properties,
            selling_plan: item.selling_plan,
          })),
        }),
      });

      const data = await response.json();
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || "Unknown error");
      }

      document.dispatchEvent(
        new CustomEvent("itemsAddedToCart", {
          detail: { data, openDrawer: openDrawer !== false },
        })
      );

      return data;
    },

    addNote(note) {
      this.sendCartUpdate({ note });
    },

    addAttribute(key, value) {
      this.sendCartUpdate({ attributes: { [key]: value } });
    },

    addMultipleAttributes(attributes) {
      this.sendCartUpdate({ attributes });
    },

    sendCartUpdate(payload) {
      fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json; charset=utf-8" },
        body: JSON.stringify(payload),
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.status) {
            console.error(
              "Cart.sendCartUpdate() :",
              data.description || data.message || "Unknown error"
            );
            return;
          }

          document.dispatchEvent(new CustomEvent("cartUpdated"));
        });
    },

    changeItemQuantity(itemId, quantity, action) {
      if (!itemId || quantity === undefined || quantity === null) {
        console.error(
          "Cart.changeItemQuantity() : itemId and quantity are required."
        );
        return;
      }

      const payload = {
        [Number.parseInt(itemId, 10) < 1000 ? "line" : "id"]: String(itemId),
        quantity: Number.parseInt(quantity, 10),
      };

      this.sendCartChange(payload);

      if (action === "plus") {
        const items = [];

        this.appendTriggerGiftItems(String(itemId).split(":")[0], 1, items)
          .then(() => {
            if (!items.length) {
              return;
            }

            setTimeout(() => {
              this.addItems(items, false).catch((error) => {
                console.error("Cart.changeItemQuantity() :", error);
              });
            }, 500);
          })
          .catch((error) => {
            console.error("Cart.changeItemQuantity() :", error);
          });
      }
    },

    changeItemProperties(itemId, properties = {}) {
      if (!itemId) {
        console.error("Cart.changeItemProperties() : itemId is required.");
        return;
      }

      if (!properties || Object.keys(properties).length === 0) {
        console.error(
          "Cart.changeItemProperties() : properties is required and cannot be empty."
        );
        return;
      }

      const payload = {
        [Number.parseInt(itemId, 10) < 1000 ? "line" : "id"]: String(itemId),
        properties,
      };

      this.sendCartChange(payload);
    },

    sendCartChange(payload) {
      fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json; charset=utf-8" },
        body: JSON.stringify(payload),
      }).then((response) => {
        if (response.status && response.status !== 200) {
          console.error(
            "Cart.sendCartChange() :",
            response.status === 404 ? "Item not in cart" : "Unknown error"
          );
          return;
        }

        document.dispatchEvent(new CustomEvent("cartUpdated"));
        if (Number.parseInt(payload.quantity, 10) === 0) {
          document.dispatchEvent(new CustomEvent("cartItemRemoved"));
        }
      });
    },
  };

  const MinicartController = {
    config: {
      drawer: document.querySelector("[data-minicart-drawer]"),
      openClass: "is-open",
      minicartSection: "legacy-minicart",
      openDrawerOnAdd: true,
      mainProductUrl: window.ThemeVars?.mainProduct?.url,
    },

    init() {
      this.setupEventListeners();
      document.dispatchEvent(new CustomEvent("minicartReady"));
    },

    setupEventListeners() {
      document.addEventListener("itemsAddedToCart", (event) => {
        this.reloadDrawer();
        if (this.config.openDrawerOnAdd && event.detail.openDrawer) {
          this.openDrawer();
        }
      });

      document.addEventListener("cartItemRemoved", () => {
        this.reloadDrawer();
      });

      document.addEventListener("cartUpdated", () => {
        this.reloadDrawer();
      });
    },

    openDrawer() {
      if (!this.config.drawer) return;
      this.config.drawer.classList.add(this.config.openClass);
      document.body.classList.add("overlay");
    },

    closeDrawer() {
      if (!this.config.drawer) return;
      this.config.drawer.classList.remove(this.config.openClass);
      document.body.classList.remove("overlay");
    },

    toggleDrawer() {
      if (!this.config.drawer) return;
      this.config.drawer.classList.toggle(this.config.openClass);
      document.body.classList.toggle("overlay");
    },

    async reloadDrawer() {
      if (!this.config.drawer) return;

      this.config.drawer.innerHTML = this.loadingScreenMarkup;
      const response = await fetch(
        `?section_id=${this.config.minicartSection}`
      );
      const html = await response.text();
      const parent = document.createElement("div");
      parent.innerHTML = html;

      const drawer = parent.querySelector("[data-minicart-drawer]");
      this.config.drawer.innerHTML = drawer ? drawer.innerHTML : "";
      this.config.drawer.querySelectorAll("script").forEach((script) => {
        const nextScript = document.createElement("script");
        nextScript.textContent = script.textContent;
        script.remove();
        this.config.drawer.appendChild(nextScript);
      });

      document.dispatchEvent(new CustomEvent("minicartReloaded"));
    },

    loadingScreenMarkup:
      '\n    \x3c!-- Loading Screen --\x3e\n    <div style="display: flex; width: 100%; height: 100%; min-height: 250px; justify-content: center; align-items: center;">\n      <div class="loader__container">\n        <div class="loader__inner">\n          <div></div>\n        </div>\n      </div>\n    </div>\n  ',
  };

  window.CartController = CartController;
  window.MinicartController = MinicartController;

  // Backwards-compatible aliases for existing theme scripts.
  window.Cart = CartController;
  window.Minicart = MinicartController;

  CartController.init();
  MinicartController.init();
})();
